import { MAP_CHANGE_TYPE, MAP_EVIDENCE_KIND } from "../../enums/map";
import type { MapBriefHandoff, MapChange } from "./mapTypes";

export function buildMapBriefHandoff(change: MapChange): MapBriefHandoff {
  return {
    changeId: change.id,
    changeType: change.type,
    currentGroup: change.currentGroupLabel,
    title: change.title,
    objective: briefObjective(change),
    currentProblem: change.currentState,
    agreedChange: change.proposedState,
    pages: change.pages,
    groups: [...new Set([change.currentGroupLabel, change.targetGroup])],
    targetRole: change.targetRole,
    relationships: change.evidence
      .filter((evidence) => evidence.kind === MAP_EVIDENCE_KIND.STRUCTURE)
      .map((evidence) => evidence.detail)
      .slice(0, 3),
    evidence: change.evidence.slice(0, 4),
    issues: change.issues.slice(0, 4),
    openQuestions: change.uncertainties,
    acceptanceCriteria: acceptanceCriteria(change),
    owner: "Not assigned",
    status: "Draft input",
  };
}

function briefObjective(change: MapChange): string {
  if (change.type === MAP_CHANGE_TYPE.CREATE_HUB) {
    return `Give the ${change.targetGroup} area one clear orientation point and routes to its represented content.`;
  }
  if (change.type === MAP_CHANGE_TYPE.CONSOLIDATE_PAGES) {
    return `Reduce overlap in ${change.targetGroup} while preserving the useful purpose of each represented page.`;
  }
  if (change.type === MAP_CHANGE_TYPE.IMPROVE_PATTERN) {
    return `Make the shared ${change.targetRole} clearer and more consistent across the represented pages.`;
  }
  if (change.type === MAP_CHANGE_TYPE.CLARIFY_DESTINATION) {
    return "Make the recorded onward route clear, useful and consistently labelled.";
  }
  if (change.type === MAP_CHANGE_TYPE.REORGANISE_GROUP) {
    return `Give ${change.targetGroup} a clearer organisation around useful page roles and relationships.`;
  }
  if (change.type === MAP_CHANGE_TYPE.RETAIN_STRUCTURE) {
    return `Retain the current ${change.targetGroup} structure with its agreed constraints recorded.`;
  }
  return "Resolve the missing strategic context before defining delivery work.";
}

function acceptanceCriteria(change: MapChange): string[] {
  if (change.type === MAP_CHANGE_TYPE.CREATE_HUB) {
    return [
      "All represented child pages have a clear route from the hub.",
      "The hub title and headings describe the section.",
      "Important recorded onward destinations are clearly labelled.",
      "Target URL and ownership are confirmed before implementation.",
    ];
  }
  if (change.type === MAP_CHANGE_TYPE.CONSOLIDATE_PAGES) {
    return [
      "One target page is agreed before any current page is removed.",
      "The useful purpose of every represented page is accounted for.",
      "Links and redirect requirements are confirmed before implementation.",
      "Content is removed only after client review of the overlap.",
    ];
  }
  if (change.type === MAP_CHANGE_TYPE.CLARIFY_DESTINATION) {
    return [
      "The intended onward destination is confirmed.",
      "Links use clear and consistent labels.",
      "Recorded broken relationships are repaired where applicable.",
      "The represented pages provide a usable route to the destination.",
    ];
  }
  if (change.type === MAP_CHANGE_TYPE.REORGANISE_GROUP) {
    return [
      "Every represented page has an agreed role in the target group.",
      "Important relationships between roles are documented.",
      "Group and navigation labels are confirmed with the client.",
      "No page is removed without a separate content decision.",
    ];
  }
  if (change.type === MAP_CHANGE_TYPE.RETAIN_STRUCTURE) {
    return [
      "The current grouping is confirmed as intentional.",
      "Known structural findings have an agreed response.",
    ];
  }
  return [
    "The shared pattern is defined for every represented page.",
    "Relevant linked findings are addressed by the pattern.",
    "Orientation and onward routes remain clear on each page.",
    "Pattern ownership is confirmed before implementation.",
  ];
}
